import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { EventData } from '../../types/event';
import { EventCard } from './EventCard';

interface EventCardListProps {
  events: EventData[];
  total: number;
  pastEventsTotal: number;
  pastEvents: EventData[];
  isPastLoading: boolean;
  isLoading: boolean;
  error: string | null;
  onDetail: (event: EventData) => void;
  onLoadMore: () => void;
  onExpandPast: () => void;
}

export const EventCardList: React.FC<EventCardListProps> = ({
  events,
  total,
  pastEventsTotal,
  pastEvents,
  isPastLoading,
  isLoading,
  error,
  onDetail,
  onLoadMore,
  onExpandPast,
}) => {
  const [pastExpanded, setPastExpanded] = useState(false);

  // メインクエリ結果からアクティブなイベントのみ表示
  // （past_only 別取得があるため、メインクエリに紛れ込んだ過去イベントは除外）
  const activeEvents = events.filter((e) => !e.is_past);

  // 過去イベントの表示対象：
  // - selectedDate（日付フィルター適用中）の場合はメインクエリから split
  // - 通常は別取得の pastEvents を使用
  const displayedPastEvents = pastEvents.length > 0
    ? pastEvents
    : events.filter((e) => e.is_past);

  const showPastSection = pastEventsTotal > 0 || displayedPastEvents.length > 0;
  const pastCount   = Math.max(pastEventsTotal, displayedPastEvents.length);
  const activeTotal = Math.max(0, total - pastCount);

  // アクティブイベントのロードがまだ残っている場合のみ「さらに読み込む」を表示
  const hasMore = activeEvents.length < activeTotal;

  const handleTogglePast = () => {
    if (!pastExpanded) {
      onExpandPast();
    }
    setPastExpanded((prev) => !prev);
  };

  if (isLoading && events.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (events.length === 0 && pastEventsTotal === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography color="text.secondary">該当するイベントがありません</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {showPastSection
          ? `開催予定 ${activeTotal}件 ／ 終了済み ${pastCount}件`
          : `${total}件`
        }
      </Typography>

      {/* アクティブなイベント */}
      {activeEvents.length > 0 ? (
        <Grid container spacing={2}>
          {activeEvents.map((event) => (
            <Grid item xs={12} md={6} key={event.id}>
              <EventCard event={event} onDetail={onDetail} />
            </Grid>
          ))}
        </Grid>
      ) : (
        events.length > 0 || !showPastSection ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
            <Typography color="text.secondary">開催予定のイベントはありません</Typography>
          </Box>
        ) : null
      )}

      {/* さらに読み込む */}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="text"
            onClick={onLoadMore}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            さらに読み込む
          </Button>
        </Box>
      )}

      {/* 終了済みイベント（折りたたみ） */}
      {showPastSection && (
        <Box mt={hasMore ? 2 : 3}>
          <Divider />
          <Button
            fullWidth
            variant="text"
            color="inherit"
            onClick={handleTogglePast}
            endIcon={pastExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              mt:             1,
              color:          'text.secondary',
              justifyContent: 'flex-start',
              textTransform:  'none',
              fontWeight:     600,
              fontSize:       '0.875rem',
            }}
          >
            終了済みのイベント（{pastCount}件）
          </Button>
          <Collapse in={pastExpanded}>
            {isPastLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : displayedPastEvents.length > 0 ? (
              <Grid container spacing={2} mt={0.5}>
                {displayedPastEvents.map((event) => (
                  <Grid item xs={12} md={6} key={event.id}>
                    <EventCard event={event} onDetail={onDetail} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box py={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  終了済みのイベントはありません
                </Typography>
              </Box>
            )}
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
